import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59]">
      <Helmet>
        <title>Privacy Policy - Cosmovex</title>
        <meta
          name="description"
          content="Privacy Policy for Cosmovex - Learn how we collect, use, and protect your data."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cosmovexapps.com/privacy-policy" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-sm transition-shadow">
                <span className="text-lg font-black text-[#221A06]">C</span>
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono">// Tech Insights</p>
            </div>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Home
            </Link>
            <Link
              to="/blogs"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Blogs
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#26313B]">Privacy Policy</h1>
          <p className="text-[#5B6B7C] mb-8">
            Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Introduction</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Welcome to Cosmovex ("we," "us," or "our"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you visit our website at cosmovexapps.com (the "Website").
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Information We Collect</h2>

              <h3 className="text-xl font-semibold mb-3 text-[#A8720E]">Automatically Collected Information</h3>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                When you visit our Website, we may automatically collect certain information about your device, including:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring URLs</li>
                <li>Pages viewed and time spent on pages</li>
                <li>Date and time of visits</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-[#A8720E]">Information You Provide</h3>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We may collect information you voluntarily provide when you:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Subscribe to our newsletter</li>
                <li>Contact us through our website</li>
                <li>Comment on blog posts</li>
                <li>Create an account (if applicable)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Use of Cookies and Tracking Technologies</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our Website. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Remember your preferences</li>
                <li>Understand how you use our Website</li>
                <li>Improve our services</li>
                <li>Deliver personalized content and advertisements</li>
              </ul>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our Website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Third-Party Advertising</h2>

              <h3 className="text-xl font-semibold mb-3 text-[#A8720E]">Google AdSense</h3>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We use Google AdSense to display advertisements on our Website. Google AdSense uses cookies and web beacons to serve ads based on your prior visits to our Website or other websites on the Internet.
              </p>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to our Website and/or other sites on the Internet. You may opt out of personalized advertising by visiting{" "}
                <a
                  href="https://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0F6BBF] hover:text-[#0C599F] underline"
                >
                  Google Ads Settings
                </a>
                .
              </p>

              <h3 className="text-xl font-semibold mb-3 text-[#A8720E]">Third-Party Vendors</h3>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to our Website. These vendors may collect information about your online activities over time and across different websites.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">How We Use Your Information</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Provide, maintain, and improve our Website</li>
                <li>Send newsletters and marketing communications (with your consent)</li>
                <li>Respond to your inquiries and requests</li>
                <li>Analyze usage patterns and trends</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
                <li>Display relevant advertisements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Data Sharing and Disclosure</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li><strong>Service Providers:</strong> Third-party companies that help us operate our Website (e.g., hosting, analytics, email services)</li>
                <li><strong>Advertising Partners:</strong> Google AdSense and other advertising networks</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Data Security</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Your Rights</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Withdraw consent at any time</li>
                <li>Data portability</li>
              </ul>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Children's Privacy</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Our Website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">International Data Transfers</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Changes to This Privacy Policy</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Contact Us</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg p-6">
                <p className="text-[#3D4B59]">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:contact@cosmovex.com" className="text-[#0F6BBF] hover:text-[#0C599F]">
                    contact@cosmovex.com
                  </a>
                </p>
                <p className="text-[#3D4B59] mt-2">
                  <strong>Website:</strong>{" "}
                  <a href="https://cosmovexapps.com" className="text-[#0F6BBF] hover:text-[#0C599F]">
                    https://cosmovexapps.com
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Additional Information for EU/EEA Residents</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                If you are located in the European Union or European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR), including:
              </p>
              <ul className="list-disc list-inside text-[#3D4B59] space-y-2 mb-4">
                <li>The right to lodge a complaint with a supervisory authority</li>
                <li>The right to restrict processing</li>
                <li>The right to object to automated decision-making</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8A93A2] text-sm">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Terms of Service
            </Link>
            <Link to="/about" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
